/**
 * Sprint TS-B — Admin TalentStart console.
 *
 * Mirrors the AdminTalentTrain pattern: company picker → KPI strip + tabs
 * via AdminTalentStartWorkspace → SuiteConsole below (which surfaces the
 * suite docs + SuiteQuestionnaireBlock that activates the already-published
 * `talentstart-onboarding-v1` questionnaire).
 *
 * Read-only by design. No write CTAs.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import AdminTalentStartWorkspace from '@/pages/admin/AdminTalentStartWorkspace';
import SuiteConsole from '@/pages/admin/SuiteConsole';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';
import { adminApi } from '@/lib/adminApi';

const STORAGE_KEY = 'cth_ts_company';

export default function AdminTalentStart() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    adminApi.listCompanies().then((cos) => {
      setCompanies(cos || []);
      const stored = localStorage.getItem(STORAGE_KEY);
      const firstId = stored && (cos || []).some((c) => c.id === stored)
        ? stored
        : (cos || [])[0]?.id;
      if (firstId) setCompanyId(firstId);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (companyId) localStorage.setItem(STORAGE_KEY, companyId);
  }, [companyId]);

  return (
    <div data-testid="admin-talentstart-root" data-company-id={companyId}>
      <div className="px-6 lg:px-8 pt-4">
        <div className="flex items-center justify-end">
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-60 h-8 text-xs" data-testid="ts-company-select">
              <SelectValue placeholder={t('common.selectCompany', { defaultValue: 'Select company' })} />
            </SelectTrigger>
            <SelectContent>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {companyId && (
        <div className="px-6 lg:px-8 pt-4 space-y-6">
          {/* Founder UAT — onboarding supervision layer (role-aware) */}
          <SuiteOpsPanel suiteCode="talentstart" onClientOpen={(cid) => setCompanyId(cid)} />
          <AdminTalentStartWorkspace companyId={companyId} />
        </div>
      )}

      {/* SuiteConsole stays — it hosts the SuiteQuestionnaireBlock that
          activates the published `talentstart-onboarding-v1` questionnaire,
          plus the suite docs. Founder directive: do not remove. */}
      <SuiteConsole
        suiteCode="talentstart" suiteName="TalentStart"
        suiteTagline="i18n:suite.taglines.talentstart" accent="accent" testid="suite-talentstart"
      />
    </div>
  );
}
